import React, { useCallback, useState } from 'react';

import CountryIcon from '~/UI/CountryIcon';
import DecimalInput from '~/UI/Input/DecimalInput';
import { PriceVolumeGrowthPriceInput } from './PriceVolumeGrowthPriceInput';
import { PriceVolumeGrowthPriceTypeIndicator } from './PriceVolumeGrowthPriceTypeIndicator';

import type { Country } from '~/Api/Country';
import TypedSelect from '~/UI/Select/TypedSelect';
import { roundedHumanFormat } from '~/utils/Price/Price';
import {
	ActualProductPackErpResponse,
	DataSourceResponse,
	ErpPackSessionPriceResponse,
	ErpPriceResponse,
	GenericTypeSerializable,
	PrescriptionTypeSerializable,
	PriceTypeSerializable,
} from 'com.cogvio.pricemonitor.api';
import Select from '~/UI/Select/Select';
import { ERPDistributionType, ERPSpecialStatus } from '~/libs/infrastructure/schemas/erp';

export const MINIMAL_GROWTH = -0.9999;
export const MINIMAL_VOLUME = 0;

const OWN_PRICE_TITLE = 'Own price';
const OWN_PRICE_VALUE = '';

export type VolumeGrowthCountryObject = {
	country: string;
	price?: string;
	isUserDefined: boolean;
	isEdited: boolean;
	yearlyVolume?: string;
	yoyGrowth?: string;
	errors?: Set<keyof VolumeGrowthCountryObject>;
	selectedPrice: string | null;
	manuallyChanged: boolean;
	distributionType: ERPDistributionType;
	specialStatus: ERPSpecialStatus;

	id: string;
	selected: boolean;
	dataSource: DataSourceResponse | null;
	genericType: GenericTypeSerializable;
	prescriptionType: PrescriptionTypeSerializable;
	actualPack: ActualProductPackErpResponse | null;
};

export type VolumeGrowthPrice = {
	country: string;
	price?: string;
	isUserDefined: boolean;
	isEdited: boolean;
	yearlyVolume?: string;
	yoyGrowth?: string;
	errors?: Set<keyof VolumeGrowthCountryObject>;
	selectedPrice: string | null;
	manuallyChanged?: boolean;
	distributionType: ERPDistributionType;
	specialStatus?: ERPSpecialStatus;

	currency?: string;
};

export interface PriceVolumeGrowthValues {
	[countryCode: string]: VolumeGrowthCountryObject;
}

export type OnVolumeGrowthChangeRequest = React.Dispatch<React.SetStateAction<PriceVolumeGrowthValues>>;

const isPriceValid = (price?: string | ErpPriceResponse): boolean => {
	return Boolean(price && typeof price === 'string' && Number(price) >= 0 && !isNaN(Number(price)));
};

const isVolumeValid = (yearlyVolume: string | null | undefined): boolean => {
	return Boolean(
		yearlyVolume &&
			typeof yearlyVolume === 'string' &&
			Number(yearlyVolume) >= MINIMAL_VOLUME &&
			!isNaN(Number(yearlyVolume))
	);
};

const isGrowthValid = (yoyGrowth: string | null | undefined): boolean =>
	Boolean(
		yoyGrowth && typeof yoyGrowth === 'string' && Number(yoyGrowth) / 100 >= MINIMAL_GROWTH && !isNaN(Number(yoyGrowth))
	);

const areValuesValid = (nextState: VolumeGrowthCountryObject): Set<keyof VolumeGrowthCountryObject> | undefined => {
	const { yoyGrowth, yearlyVolume, price, manuallyChanged } = nextState;
	const errors: (keyof VolumeGrowthCountryObject)[] = [];

	if (!isPriceValid(price) && (manuallyChanged || yoyGrowth || yearlyVolume)) {
		errors.push('price');
	}

	if (yearlyVolume && !isVolumeValid(yearlyVolume)) {
		errors.push('yearlyVolume');
	}

	if (yoyGrowth && !isGrowthValid(yoyGrowth)) {
		errors.push('yoyGrowth');
	}

	return new Set(errors);
};

export interface PriceVolumeGrowthModalRowProps {
	availablePrices: ErpPackSessionPriceResponse[];
	priceType: PriceTypeSerializable;
	country: Country;
	values: VolumeGrowthPrice | undefined;
	isHospitalRelevant: boolean;
	onVolumeGrowthChangeRequest: OnVolumeGrowthChangeRequest;
}

export function PriceVolumeGrowthModalRow({
	availablePrices,
	country,
	priceType,
	values,
	isHospitalRelevant,
	onVolumeGrowthChangeRequest,
}: PriceVolumeGrowthModalRowProps) {
	const [showEditing, setEditing] = useState(false);

	const changeHandler = useCallback(
		(update: Partial<VolumeGrowthCountryObject>) => {
			onVolumeGrowthChangeRequest((prev) => {
				const nextValues = {
					...(prev[country.alpha2] || {}),
					...update,
				};

				const originalPrice = availablePrices.find(
					(packs: ErpPackSessionPriceResponse) => packs.id === update?.selectedPrice
				);

				if (originalPrice) {
					return {
						...prev,
						[country.alpha2]: {
							...nextValues,
							distributionType: originalPrice.distributionType as ERPDistributionType,
							isEdited: true,
							isUserDefined: false,
							specialStatus: originalPrice?.specialStatus,
							price: String(originalPrice?.price.price.value),
							manuallyChanged: false,
						},
					};
				}

				return {
					...prev,
					[country.alpha2]: {
						...nextValues,
						manuallyChanged: true,
						isEdited: true,
						errors: areValuesValid(nextValues),
					},
				};
			});
		},
		[availablePrices, country.alpha2, onVolumeGrowthChangeRequest]
	);

	const yearlyVolumeValue = values?.yearlyVolume || '';
	const specialStatus = values?.specialStatus || {
		code: 'no-special-status',
		name: 'No special status',
	};
	const yearlyGrowthValue = values?.yoyGrowth || '';
	const selectedPriceId = values?.selectedPrice || '';
	const distributionType = values?.distributionType ?? ERPDistributionType.RETAIL;
	const storedUserDefinedPrice = availablePrices.find((price) => price.isUserDefined);
	const isSelectedPricedDefinedManually = selectedPriceId === storedUserDefinedPrice?.id;

	// NOTE: isUserDefined = true when user is editing the price in-place but olso when user defined price comes from API
	// this means that we want to change price in place when user is editing price and when stored price comes from api only.
	// At that momement manuallyChanged = true.
	const displayedPrice =
		values?.isUserDefined && isSelectedPricedDefinedManually
			? values?.price || ''
			: availablePrices.find((price) => price.id === selectedPriceId)?.price.price.value || values?.price || null;

	const onlyOwnPrices = availablePrices.every(({ isUserDefined }) => isUserDefined);

	return (
		<tr>
			<td className='pl-3 pr-2 py-1 font-display text-label font-semibold text-gray-800 leading-none border-b border-gray-200'>
				<div className='flex items-center'>
					<CountryIcon className='w-4 h-4 mr-1.5' code={country.alpha2} />
					<div>
						{country.name}
						{availablePrices.length > 0 && !onlyOwnPrices && (
							<div className='mt-1 -ml-1'>
								<TypedSelect
									className='p-0 font-body text-label w-24 truncate cursor-pointer'
									options={[
										...(!storedUserDefinedPrice
											? [
													{
														value: OWN_PRICE_VALUE,
														title: OWN_PRICE_TITLE,
													},
											  ]
											: []),
										...availablePrices
											.map((price) => ({
												value: price.id,
												title: price.isUserDefined
													? OWN_PRICE_TITLE
													: `${price.dataSource?.shortName}, ${
															price.actualPack?.companyName || ''
													  }-${roundedHumanFormat(price.price.price.currency, price.price.price.value.toString())}`,
											}))
											.sort((a) => (a.title === OWN_PRICE_TITLE ? -1 : 1)),
									]}
									value={!values?.isUserDefined ? selectedPriceId : OWN_PRICE_VALUE}
									onTypedValueChange={(nextSelectedPrice) => {
										if (nextSelectedPrice === OWN_PRICE_VALUE || nextSelectedPrice === storedUserDefinedPrice?.id) {
											changeHandler({
												selectedPrice: null,
												price: String(displayedPrice) || undefined,
											});
											setEditing(true);
											return;
										}

										setEditing(false);
										changeHandler({
											selectedPrice: nextSelectedPrice,
										});
									}}
								/>
							</div>
						)}
					</div>
				</div>
			</td>
			<td className='px-2 py-1.5 border-b border-gray-200 w-28'>
				<div className='flex items-center justify-between'>
					<PriceVolumeGrowthPriceInput
						showEditing={showEditing}
						userDefined={!!values?.isUserDefined}
						value={displayedPrice}
						onChange={(price: string) => {
							changeHandler({
								isUserDefined: true,
								selectedPrice: null,
								price: String(price),
							});
						}}
						onEditingSet={() => setEditing(true)}
						error={values?.errors?.has('price')}
					/>
				</div>
			</td>
			<td className='border-b border-gray-200 text-center'>
				<PriceVolumeGrowthPriceTypeIndicator priceType={priceType} />
			</td>
			<td className='pl-1 py-1.5 border-b border-gray-200'>
				{!isHospitalRelevant || !Boolean(values?.price) ? (
					<span className='pl-1 text-labelsmall font-regular text-gray-400 leading-5 capitalize'>
						{distributionType}
					</span>
				) : (
					<Select
						value={distributionType}
						onChange={(e) => {
							changeHandler({
								isUserDefined: true,
								distributionType: e.currentTarget.value as ERPDistributionType,
							});
						}}
						disabled={!isHospitalRelevant || !Boolean(values?.price)}
						className='px-0 py-1 w-full rounded-sm font-body text-labelsmall font-semibold text-gray-700 truncate cursor-pointer capitalize'
					>
						{Object.values(ERPDistributionType).map((value) => (
							<option key={value} value={value}>
								{value}
							</option>
						))}
					</Select>
				)}
			</td>
			<td className='pl-1 py-1.5 border-b border-gray-200'>
				<span className='pl-1 text-labelsmall font-regular text-gray-400 leading-5 capitalize'>
					{specialStatus.name === 'No special status' ? 'Universal' : specialStatus.name}
				</span>
			</td>
			<td className='px-2 py-1.5 border-b border-gray-200'>
				<DecimalInput
					allowEmpty
					selectOnFocus
					className='w-full px-1.5 py-1 font-display text-label text-black font-semibold leading-4 border border-gray-300 rounded tabular-nums'
					hasError={values?.errors?.has('yearlyVolume')}
					min={MINIMAL_VOLUME}
					type='text'
					value={yearlyVolumeValue}
					onDecimalInput={(yearlyVolume) => {
						changeHandler({
							yoyGrowth: yearlyGrowthValue,
							yearlyVolume,
						});
					}}
				/>
			</td>
			<td className='pr-3 pl-2 py-1.5 border-b border-gray-200'>
				<DecimalInput
					allowEmpty
					negative
					selectOnFocus
					className='w-full px-1.5 py-1 font-display text-label text-black font-semibold leading-4 border border-gray-300 rounded tabular-nums'
					hasError={values?.errors?.has('yoyGrowth')}
					min={MINIMAL_GROWTH}
					type='text'
					value={yearlyGrowthValue}
					onDecimalInput={(yoyGrowth) => {
						changeHandler({
							yearlyVolume: yearlyVolumeValue,
							yoyGrowth,
						});
					}}
				/>
			</td>
		</tr>
	);
}
